import { sanitizeRedirectPath } from './redirectAfterLogin';

/**
 * An action a signed-out visitor started (save, add to calendar, write a Pick review)
 * that runs once after
 * they sign in or register and land back on the same page (PRD F1, tech design §5.5).
 * Stored in sessionStorage so it survives the Google round trip and email confirmation.
 * Only these types exist, each is read once, and it expires after 30 minutes.
 */
export type PendingAction =
  | { type: 'favorite'; targetType: 'activity' | 'pick'; targetId: string }
  | { type: 'calendar'; pickId: string; occurrenceId: string }
  | { type: 'review'; pickId: string };

interface StoredPendingAction {
  action: PendingAction;
  returnTo: string;
  expiresAt: number;
}

const KEY = 'yoyo_pending_action';
const TTL_MS = 30 * 60 * 1000;

function isPendingAction(v: unknown): v is PendingAction {
  if (!v || typeof v !== 'object') return false;
  const a = v as Record<string, unknown>;
  if (a.type === 'favorite') {
    return (
      (a.targetType === 'activity' || a.targetType === 'pick') && typeof a.targetId === 'string'
    );
  }
  if (a.type === 'calendar') {
    return typeof a.pickId === 'string' && typeof a.occurrenceId === 'string';
  }
  if (a.type === 'review') return typeof a.pickId === 'string';
  return false;
}

export function savePendingAction(action: PendingAction, returnTo: string, now = Date.now()): void {
  const safe = sanitizeRedirectPath(returnTo);
  if (!safe) return;
  const stored: StoredPendingAction = { action, returnTo: safe, expiresAt: now + TTL_MS };
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode): the visitor simply repeats the action.
  }
}

/**
 * Returns the stored action when it belongs to this page (and type, if given), removing
 * it so it runs once. A stored action for another page is left alone; an expired or
 * malformed one is dropped.
 */
export function takePendingAction<T extends PendingAction['type']>(
  currentPath: string,
  type?: T,
  now = Date.now()
): Extract<PendingAction, { type: T }> | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let stored: StoredPendingAction | null = null;
  try {
    stored = JSON.parse(raw) as StoredPendingAction;
  } catch {
    stored = null;
  }
  if (
    !stored ||
    !isPendingAction(stored.action) ||
    typeof stored.expiresAt !== 'number' ||
    stored.expiresAt < now
  ) {
    window.sessionStorage.removeItem(KEY);
    return null;
  }
  if (stored.returnTo !== currentPath) return null;
  if (type && stored.action.type !== type) return null;
  window.sessionStorage.removeItem(KEY);
  return stored.action as Extract<PendingAction, { type: T }>;
}
